import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { motion, useMotionValue } from "framer-motion";
import { color, font, z } from "./tokens";
import { ERA_SECTIONS } from "./data";
import AnimatedIcon from "./icons";
import { STATIC_MODE } from "./primitives";
import useMedia from "./useMedia";

const RAIL_HEIGHT = "min(52vh, 460px)";

const Rail = styled.div<{ $hidden: boolean }>`
  position: fixed;
  left: clamp(18px, 2.2vw, 34px);
  top: 50%;
  transform: translateY(-50%);
  height: ${RAIL_HEIGHT};
  z-index: ${z.nav - 1};
  display: flex;
  gap: 14px;
  opacity: ${(p) => (p.$hidden ? 0 : 1)};
  transition: opacity 0.45s cubic-bezier(0.16, 1, 0.3, 1);
  pointer-events: none;
`;

const Track = styled.div`
  position: relative;
  width: 2px;
  height: 100%;
  background: rgba(14, 14, 12, 0.14);
  border-radius: 1px;
`;

const Fill = styled(motion.div)`
  position: absolute;
  inset: 0;
  background: ${color.accent};
  border-radius: 1px;
  transform-origin: top center;
  will-change: transform;
`;

const Nodes = styled.div`
  position: absolute;
  inset: 0;
`;

const Node = styled.div<{ $at: number }>`
  position: absolute;
  top: ${(p) => p.$at}%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
`;

const Dot = styled.div<{ $lit: boolean }>`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => (p.$lit ? color.paper : color.paper)};
  border: 1.5px solid ${(p) => (p.$lit ? color.ink : "rgba(14, 14, 12, 0.22)")};
  color: ${color.ink};
  transition: border-color 0.4s cubic-bezier(0.16, 1, 0.3, 1);
`;

const Label = styled.div<{ $lit: boolean }>`
  position: absolute;
  left: 34px;
  white-space: nowrap;
  font-family: ${font.mono};
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${(p) => (p.$lit ? color.ink : "rgba(14, 14, 12, 0.38)")};
  transition: color 0.4s cubic-bezier(0.16, 1, 0.3, 1);

  /* Only when the gutter is wide enough that labels can't collide with
     content — below that the icon dots carry the timeline alone. */
  display: none;
  @media (min-width: 1600px) {
    display: block;
  }
`;

/* Viewport fraction where a section "takes over" the rail — an era lights
   up once its top crosses this line. */
const TRIGGER = 0.4;

/**
 * Vertical career timeline pinned to the left edge — era bullets draw
 * themselves in as the page scrolls past each era; the line floods with
 * accent. Anchored to the real [data-era] sections so it always matches
 * what's on screen, however tall each era is. Desktop only.
 */
const TimelineRail: React.FC = () => {
  const wide = useMedia("(min-width: 1200px)");
  const [active, setActive] = useState(STATIC_MODE ? 2 : 0);
  // The paper-era timeline has no business inside the ink interludes
  // (mission board, footer) — fade it out while one covers the rail.
  const [overInk, setOverInk] = useState(false);

  const fill = useMotionValue(0);

  useEffect(() => {
    if (STATIC_MODE) return;
    const check = () => {
      const trigger = window.innerHeight * TRIGGER;

      // Era tops in viewport space, in era order. The lit node is the
      // last era whose top has crossed the trigger line; the fill
      // interpolates between successive era tops so it reaches node i
      // exactly as era i takes over.
      const tops = Array.from(
        document.querySelectorAll<HTMLElement>("[data-era]"),
      )
        .sort((a, b) => Number(a.dataset.era) - Number(b.dataset.era))
        .map((el) => el.getBoundingClientRect().top);

      if (tops.length > 1) {
        const last = tops.length - 1;
        let idx = -1;
        while (idx < last && tops[idx + 1] <= trigger) idx++;
        setActive(Math.max(0, idx));

        let f = 0;
        if (idx >= last) {
          f = 1;
        } else if (idx >= 0) {
          const span = tops[idx + 1] - tops[idx];
          const part = span > 0 ? (trigger - tops[idx]) / span : 0;
          f = (idx + Math.min(1, Math.max(0, part))) / last;
        }
        fill.set(f);
      }

      const mid = window.innerHeight * 0.5;
      const inks = document.querySelectorAll<HTMLElement>("[data-ink]");
      let over = false;
      inks.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.top < mid && r.bottom > mid) over = true;
      });
      setOverInk(over);
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [fill]);

  if (!wide) return null;

  const count = ERA_SECTIONS.length;

  return (
    <Rail aria-hidden $hidden={overInk}>
      <Track>
        <Fill style={{ scaleY: STATIC_MODE ? 0.45 : fill }} />
        <Nodes>
          {ERA_SECTIONS.map((era, i) => {
            const lit = i <= active;
            return (
              <Node key={era.number} $at={(i / (count - 1)) * 100}>
                <Dot $lit={lit}>
                  <AnimatedIcon
                    name={era.icon}
                    size={13}
                    strokeWidth={2.2}
                    active={lit}
                  />
                </Dot>
                <Label $lit={lit}>
                  {era.startYear} {era.code}
                </Label>
              </Node>
            );
          })}
        </Nodes>
      </Track>
    </Rail>
  );
};

export default TimelineRail;
